import { useSyncExternalStore } from 'react';

export const RAILS_KEY = 'vudget-dial-rails';
/** `app_settings` row — a way of working, not a property of the screen. */
export const RAILS_DB_KEY = 'dial_rails';

/**
 * Whether the dial table draws rails at all.
 *
 * A slider is the right control for *exploring* — drag it and watch the meter
 * move. It is the wrong control for *landing on a number*, and it is expensive:
 * `.vd-slider` buys its 44px touch target with `padding: 20px 0; margin: -20px
 * 0`, so every row carries a band of hit area whether or not you ever drag it,
 * and on a phone the rail claims a line of its own beneath the figures. Across
 * sixty-odd expenses that is most of the table's height spent on a control you
 * may have finished using.
 *
 * Turning them off is only viable because there are now two typed inputs per
 * row — the amount and the percentage. Before those, hiding the rails would
 * have left rows with no way to edit them at all, which is why this is landing
 * now and not earlier.
 *
 * Off is not a "compact mode" and deliberately does not change type sizes,
 * padding or which columns show. It removes one control. Bundling a density
 * change into it would make the toggle mean two things and be reachable for
 * neither.
 *
 * ── Why it persists like the basis, not like the mask ──
 *
 * The privacy mask is localStorage-only on purpose: whether the phone hides
 * amounts on a train says nothing about the laptop at home. This is not that.
 * It is how you prefer to drive the table, and it applies at every viewport by
 * design — the request was explicitly "all views at any viewport", so this is
 * deliberately *not* split per form factor the way `useUiScale` is. So D1 is
 * the source of truth and localStorage is the first-paint cache, exactly like
 * `useNetBasis`.
 */
const read = (): boolean => {
  try {
    // Absent means on. Rails are the long-standing behaviour, and a preference
    // nobody has expressed should not quietly remove a control.
    return localStorage.getItem(RAILS_KEY) !== 'off';
  } catch {
    return true;
  }
};

let rails: boolean = read();
const listeners = new Set<() => void>();

function write(next: boolean): void {
  if (next === rails) return;
  rails = next;
  try { localStorage.setItem(RAILS_KEY, next ? 'on' : 'off'); } catch { /* private mode */ }
  listeners.forEach(l => l());
}

export function setDialRails(next: boolean): void {
  write(next);
}

export function toggleDialRails(): void {
  write(!rails);
}

/**
 * Adopt the stored value without treating it as a user action.
 *
 * `usePreferences` calls this once on boot so hydration doesn't echo straight
 * back to the server as though the toggle had been pressed.
 */
export function hydrateDialRails(next: string | undefined | null): void {
  if (next !== 'on' && next !== 'off') return;
  write(next === 'on');
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const getSnapshot = () => rails;

export const getDialRails = (): boolean => rails;

export function useDialRails(): { rails: boolean; toggle: () => void } {
  return { rails: useSyncExternalStore(subscribe, getSnapshot, getSnapshot), toggle: toggleDialRails };
}
